import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactElement } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth, useNavigate } from '@/lib/legacy';
import { CollateralModal as CollateralModalJs } from '../../pages/CollateralPage';
import { ToolboxIcon } from './navIcons';
import { EXPAND_AT, subItemIcons, visibleNavItems, type NavItem, type NavSubItem } from './navItems';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/CollateralPage/CollateralPage.jsx
const CollateralModal = CollateralModalJs as any as (props: { open: boolean; onClose: () => void }) => JSX.Element;

/**
 * The app's sidebar. Which tabs show, which one is current and how the Admin and Aggregation
 * submenus open and close are today's DashboardTabs rules, kept exactly (D23, D24): clicking Admin
 * opens its submenu; clicking Aggregation from another tab navigates with its submenu closed, and
 * a second click opens it.
 */
export function AppSidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const match = useRouteMatch<{ tab: string }>('/:tab');
  const { isMobile, state, setOpen, openMobile, setOpenMobile } = useSidebar();
  const [collateralOpen, setCollateralOpen] = useState(false);

  const roles = useMemo(() => (user && Array.isArray(user.roles) ? user.roles : []), [user]);
  const items = useMemo(() => visibleNavItems(roles), [roles]);

  const tabParam = match && items.some((item) => item.name === match.params.tab) ? match.params.tab : null;
  const [currentTab, setCurrentTab] = useState<string | null>(tabParam);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const shouldNavigate = useRef(false);

  useEffect(() => {
    if (tabParam && !currentTab) {
      setCurrentTab(tabParam);
    } else if (tabParam !== currentTab) {
      if (currentTab && shouldNavigate.current) {
        shouldNavigate.current = false;
        navigate.to({ pathname: `/${currentTab}`, state: { tab: currentTab } });
      } else {
        setCurrentTab(tabParam ? tabParam : null);
      }
    } else {
      // The route is on the chosen tab (a submenu item navigates there itself), so nothing is
      // left to navigate to; a later route change that isn't a sidebar click must not redirect (D33).
      shouldNavigate.current = false;
    }
  }, [currentTab, tabParam, navigate]);

  // Each of today's two sidebars (icon rail and full) kept its own submenu state; switching
  // between them started with every submenu closed.
  useEffect(() => {
    setOpenSubmenu(null);
  }, [state]);

  // Choosing an item closes the drawer, and below the expand width folds the sidebar back to icons.
  const closeAfterChoosing = useCallback(() => {
    if (isMobile) {
      if (openMobile) setOpenMobile(false);
    } else if (window.innerWidth < EXPAND_AT && state === 'expanded') {
      setOpen(false);
    }
  }, [isMobile, openMobile, setOpenMobile, setOpen, state]);

  const select = (name: string) => {
    shouldNavigate.current = name !== tabParam;
    if (name !== currentTab || shouldNavigate.current) {
      setCurrentTab(name);
      setOpenSubmenu(name === 'admin' ? 'admin' : null);
    }
    closeAfterChoosing();
  };

  const chooseItem = (event: MouseEvent, item: NavItem) => {
    event.preventDefault();
    if (item.submenu) {
      setOpenSubmenu((open) => (open === item.name ? null : item.name));
    }
    select(item.name);
  };

  const chooseSubItem = (event: MouseEvent, item: NavItem, sub: NavSubItem) => {
    event.preventDefault();
    navigate.to(sub.path);
    select(item.name);
  };

  const iconMode = !isMobile && state === 'collapsed';

  return (
    <>
      <Sidebar collapsible="icon" className="z-[1200]">
        <SidebarHeader className="flex-row items-center">
          <img src="/images/understory_logo_white.png" alt="" className="size-10 group-data-[collapsible=icon]:hidden" />
          {/* The legacy LogoNavGroup title: Centra No2 light, uppercase, 1px tracking, text.16 (16/28). */}
          <span className="font-brand text-base font-light tracking-[1px] text-sidebar-foreground uppercase group-data-[collapsible=icon]:hidden">
            Understory
          </span>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <nav aria-label="Main">
              <SidebarMenu>
                {items.map((item) => {
                  const selected = currentTab === item.name;
                  const Icon = item.icon;
                  const open = openSubmenu === item.name && !!item.submenu;
                  const link = (
                    <SidebarMenuButton
                      isActive={selected}
                      aria-current={selected ? 'page' : undefined}
                      className="h-9 px-2.5 text-[15px] text-sidebar-foreground/65 data-active:text-sidebar-foreground"
                      render={<a href={`/${item.name}`} />}
                      onClick={(event: MouseEvent) => chooseItem(event, item)}
                    >
                      <Icon aria-hidden />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  );

                  if (item.submenu && iconMode) {
                    return (
                      <SidebarMenuItem key={item.name}>
                        <RailSubmenu
                          link={link}
                          open={open}
                          onClose={() => setOpenSubmenu(null)}
                          items={item.submenu.map((sub) => ({
                            ...sub,
                            current: location.pathname.startsWith(sub.path),
                            onChoose: (event: MouseEvent) => chooseSubItem(event, item, sub),
                          }))}
                        />
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.name}>
                      {link}
                      {item.submenu ? (
                        <Collapsible open={open}>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.submenu.map((sub) => {
                                const active = location.pathname.startsWith(sub.path);
                                const SubIcon = subItemIcons[sub.path];
                                return (
                                  <SidebarMenuSubItem key={sub.path}>
                                    <SidebarMenuSubButton
                                      href={sub.path}
                                      isActive={active}
                                      aria-current={active ? 'page' : undefined}
                                      className="h-9 px-2.5 text-[15px] text-sidebar-foreground/80 data-active:bg-primary-dark data-active:text-sidebar-foreground"
                                      onClick={(event: MouseEvent) => chooseSubItem(event, item, sub)}
                                    >
                                      {SubIcon ? <SubIcon aria-hidden /> : null}
                                      <span>{sub.label}</span>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </nav>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="group-data-[collapsible=icon]:hidden">
          <SidebarSeparator />
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                className="h-9 px-2.5 text-[15px] text-sidebar-foreground"
                onClick={() => {
                  closeAfterChoosing();
                  setCollateralOpen((open) => !open);
                }}
              >
                <span>Collateral</span>
                <ToolboxIcon aria-hidden />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      {/* Outside the sidebar, so closing the mobile drawer leaves the dialog open. */}
      <CollateralModal open={collateralOpen} onClose={() => setCollateralOpen(false)} />
    </>
  );
}

type RailSubItem = NavSubItem & { current: boolean; onChoose: (event: MouseEvent) => void };

/**
 * A submenu parent in the icon rail: the item stays a link (it navigates, as everywhere else), and
 * its submenu opens beside it as a flyout anchored to the link rather than triggered by it.
 */
function RailSubmenu({ link, open, onClose, items }: { link: ReactElement; open: boolean; onClose: () => void; items: RailSubItem[] }) {
  const anchor = useRef<HTMLDivElement>(null);
  return (
    <>
      <div ref={anchor}>{link}</div>
      <DropdownMenu
        open={open}
        onOpenChange={(next, details) => {
          // A press on the link itself is the link's to handle: it toggles the submenu.
          const target = (details?.event?.target ?? null) as Node | null;
          if (!next && target && anchor.current?.contains(target)) return;
          if (!next) onClose();
        }}
      >
        <DropdownMenuContent anchor={anchor} side="right" align="start" className="w-56">
          {items.map((sub) => (
            <DropdownMenuItem key={sub.path} render={<a href={sub.path} />} aria-current={sub.current ? 'page' : undefined} onClick={sub.onChoose}>
              {sub.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
